/**
 * playerManagement.ts - Dynamic player input cards (step 1) and the
 * per-player options cards (step 2), built from <template> elements.
 */
import { state } from './config';
import { bindInputName } from './utils';
import { updateCartSummary } from './cart';

export function initPlayerManagement(): void {
    const addPlayerBtn = document.querySelector('.js-btn-add-player');
    if (addPlayerBtn) {
        addPlayerBtn.addEventListener('click', () => addPlayerInputCard());
    }

    const container = document.getElementById('PlayerInputsContainer');
    if (container) {
        container.addEventListener('input', (e: Event) => {
            const target = e.target as HTMLElement;
            if (target.classList.contains('js-first-name') || target.classList.contains('js-last-name')) {
                updateCartSummary();
            }
        });
    }
}

export function addPlayerInputCard(): void {
    const template = document.getElementById('PlayerInputTemplate') as HTMLTemplateElement | null;
    if (!template) return;

    const index = document.querySelectorAll('.player-card').length;
    const clone = template.content.cloneNode(true) as DocumentFragment;
    const card = clone.querySelector('.player-card') as HTMLElement;

    card.setAttribute('data-player-index', index.toString());

    const title = card.querySelector('.js-player-title');
    if (title) title.textContent = `Player ${index + 1} Details`;

    bindInputName(card, '.js-uscf-input', `Players[${index}].UscfId`);
    bindInputName(card, '.js-rating-input', `Players[${index}].Rating`);
    bindInputName(card, '.js-first-name', `Players[${index}].FirstName`);
    bindInputName(card, '.js-last-name', `Players[${index}].LastName`);
    bindInputName(card, '.js-dob-input', `Players[${index}].DateOfBirth`);
    bindInputName(card, '.js-grade-input', `Players[${index}].Grade`);

    const removeBtn = card.querySelector('.js-btn-remove-player');
    if (index > 0 && removeBtn) {
        removeBtn.classList.remove('d-none');
        removeBtn.addEventListener('click', () => {
            card.remove();
            reindexPlayerCards();
            updateCartSummary();
        });
    }

    const container = document.getElementById('PlayerInputsContainer');
    if (container) {
        container.appendChild(clone);
        state.playersCount = document.querySelectorAll('.player-card').length;
        updateCartSummary();
    }
}

export function reindexPlayerCards(): void {
    const cards = document.querySelectorAll<HTMLElement>('.player-card');
    cards.forEach((card, idx) => {
        card.setAttribute('data-player-index', idx.toString());

        const title = card.querySelector('.js-player-title');
        if (title) title.textContent = `Player ${idx + 1} Details`;

        bindInputName(card, '.js-uscf-input', `Players[${idx}].UscfId`);
        bindInputName(card, '.js-rating-input', `Players[${idx}].Rating`);
        bindInputName(card, '.js-first-name', `Players[${idx}].FirstName`);
        bindInputName(card, '.js-last-name', `Players[${idx}].LastName`);
        bindInputName(card, '.js-dob-input', `Players[${idx}].DateOfBirth`);
        bindInputName(card, '.js-grade-input', `Players[${idx}].Grade`);
    });

    state.playersCount = cards.length;
}

export function syncStep2PlayerOptions(): void {
    const container = document.getElementById('PlayerOptionsContainer');
    const template = document.getElementById('PlayerOptionsTemplate') as HTMLTemplateElement | null;
    if (!container || !template) return;

    container.innerHTML = '';

    const playerCards = document.querySelectorAll<HTMLElement>('.player-card');

    playerCards.forEach((card, idx) => {
        const firstName = card.querySelector<HTMLInputElement>('.js-first-name')?.value.trim() || `Player ${idx + 1}`;
        const lastName = card.querySelector<HTMLInputElement>('.js-last-name')?.value.trim() || '';
        const fullName = `${firstName} ${lastName}`.trim();

        const clone = template.content.cloneNode(true) as DocumentFragment;
        const optionCard = clone.querySelector('.js-player-option-card') as HTMLElement;

        optionCard.setAttribute('data-player-option-index', idx.toString());

        const title = optionCard.querySelector('.js-player-option-title');
        if (title) title.textContent = `${fullName} Options`;

        const sections = optionCard.querySelectorAll<HTMLInputElement>('.js-section-radio');
        sections.forEach((radio, sIdx) => {
            radio.name = `Players[${idx}].SelectedSectionId`;
            radio.id = `Section_${idx}_${sIdx}`;
            const label = radio.nextElementSibling as HTMLLabelElement | null;
            if (label) label.setAttribute('for', `Section_${idx}_${sIdx}`);
            if (sIdx === 0) radio.checked = true;
        });

        const byes = optionCard.querySelectorAll<HTMLInputElement>('.js-bye-checkbox');
        byes.forEach((bye) => {
            const rd = bye.value;
            bye.name = `Players[${idx}].RequestedByes`;
            bye.id = `Bye_${rd}_${idx}`;
            const label = bye.nextElementSibling as HTMLLabelElement | null;
            if (label) label.setAttribute('for', `Bye_${rd}_${idx}`);
        });

        bindInputName(optionCard, '.js-payout-method', `Players[${idx}].PayoutMethod`);
        bindInputName(optionCard, '.js-payout-account', `Players[${idx}].PayoutAccount`);

        container.appendChild(clone);
    });
}